/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {GlobalScenario} from '../models/global-scenario-dto.model';
import {GlobalScenarioRequest} from '../models/global-scenario-request-dto.model';
import {VehicleScenario} from '../models/vehicle-scenario-dto.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})

export class GlobalScenarioService {
  public globalScenario: GlobalScenario;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Sends a get request to retrieve list of all global scenarios
   */
  getAllGlobalScenarios(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}global-scenario`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of member vehicle scenarios
   * @param globalScenarioId global scenario id
   */
  getMemberVehicleScenarios(globalScenarioId: number): any {
    const params = new HttpParams().set('globalScenarioId', globalScenarioId);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}global-scenario/member/vehscen`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of member vehicle scenarios that the user has access to
   * @param globalScenarioId global scenario id
   * @param gmid the user id
   */
  getMemberVehicleScenariosByGmid(globalScenarioId: number, gmid: string): any {
    const params = new HttpParams()
    .set('globalScenarioId', globalScenarioId)
    .set('gmid', gmid);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}global-scenario/member/vehscen`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of member vehicle scenarios
   * @param globalScenarioId global scenario id
   * @param gmid gmid of the user
   */
  getMemberIndustryScenarios(globalScenarioId: number, gmid: string): any {
    const params = new HttpParams()
    .set('globalScenarioId', globalScenarioId)
    .set('gmid', gmid);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}global-scenario/member/indscen`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to create a global scenario
   * @param globalScenarioName global scenario name
   * @param gmid gmid
   * @param vehicleScenarios vehicle scenario list
   */
  createGlobalScenario(globalScenarioName: string, gmid: string, vehicleScenarios: VehicleScenario[]): Observable<any> {
    const params = new HttpParams().set('globalScenarioName', globalScenarioName).set('gmid', gmid);
    return this.http.post(`${this.configService.baseApiUrl}global-scenario/create`, vehicleScenarios,
      {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a put request to update the global scenario by the global scenario id
   * @param globalScenarioRequestDto global scenario request dto
   * @param isNameChange boolean to determine is the global scenario name has been changes
   */
  updateGlobalScenario(globalScenarioRequestDto: GlobalScenarioRequest, isNameChange: boolean): Observable<any> {
    const params = new HttpParams().set('isNameChange', isNameChange);
    return this.http.post(`${this.configService.baseApiUrl}global-scenario/update`,
      globalScenarioRequestDto, {params}).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a delete request to delete the global scenario from the GLBL_SCENARIO table by the global scenario id
   * @param globalScenarioId global scenario id
   */
  deleteGlobalScenarioById(globalScenarioId: number): any {
    const params = new HttpParams().set('globalScenarioId', globalScenarioId);
    return this.http.delete<any>(`${this.configService.baseApiUrl}global-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the year range of a global scenario
   * @param globalScenarioId the global scenario id
   * @param userId the GMID of the user
   * @param excludeNonGM whether to exclude years from NON-GM scenarios that are attached to the global scenario
   */
  getYearRange(globalScenarioId: number, userId: string, excludeNonGM: boolean): Observable<any> {
    const params = new HttpParams()
    .set('globalScenId', globalScenarioId)
    .set('userId', userId)
    .set('excludeNonGM', excludeNonGM);
    return this.http.get<any>(`${this.configService.baseApiUrl}global-scenario/year-range`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the Global Propulsion Record Report
   * @param gmid the id of the user
   * @param globalScenId the global scenario id
   */
  globalPropulsionRecordReport(gmid: string, globalScenId: number) {
    const params = new HttpParams()
    .set('globalScenId', globalScenId)
    .set('gmid', gmid)
    return this.http.get<any>(`${this.configService.baseApiUrl}global-scenario/propulsion-record-report`,
      {params: params, headers: new HttpHeaders(), responseType: 'blob' as 'json'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the Global Vehicle Forecast Template
   * @param gmid the id of the user
   * @param globalScenId the global scenario id
   * @param startYear the start year
   * @param endYear the end year
   */
  globalVehicleForecastTemplate(gmid: string, globalScenId: number, startYear, endYear) {
    const params = new HttpParams()
    .set('global-scen-id', globalScenId)
    .set('gmid', gmid)
    .set('start-year', startYear)
    .set('end-year', endYear);
    return this.http.get<any>(`${this.configService.baseApiUrl}global-scenario/forecast-template/vehicle`,
      {params: params, headers: new HttpHeaders(), responseType: 'blob' as 'json'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets a file for the global scenario associations
   * @param globalScenId
   */
  getGlobalScenarioAssociations(globalScenId: number) {
    const params = new HttpParams().set('global-scen-id', globalScenId);
    return this.http.get<any>(`${this.configService.baseApiUrl}global-scenario/associations`,
      {params: params, headers: new HttpHeaders(), responseType: 'blob' as 'json'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }

}
